<template>
    <div class="sec">
        <div class="sec-main">
            <header class="sec-project-name-bar">
                <span class="sec-project-name-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.65">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8M8 17h8" />
                    </svg>
                </span>
                <h1 class="sec-project-name-title">{{ currentProject.name || '当前项目' }}</h1>
            </header>

            <!-- 列表 -->
            <div v-show="view === 'list'" class="sec-panel">
                <div class="sec-toolbar">
                    <el-form :inline="true" class="sec-filter-bar" @submit.native.prevent>
                        <el-form-item>
                            <el-input v-model.trim="keyword" size="small" clearable placeholder="搜索任务名称..."
                                suffix-icon="el-icon-search" style="width: 220px" @input="onKeywordInput"
                                @clear="onFilterChange" />
                        </el-form-item>
                        <el-form-item>
                            <el-select v-model="subjectId" size="small" clearable placeholder="全部学科"
                                style="width: 140px" @change="onFilterChange">
                                <el-option label="全部学科" value="" />
                                <el-option v-for="item in subjectOptions" :key="item.id" :label="item.name"
                                    :value="item.id" />
                            </el-select>
                        </el-form-item>
                    </el-form>
                    <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">创建组卷任务</el-button>
                </div>
                <div class="sec-panel-body" v-loading="listLoading">
                    <el-table :data="records" stripe class="sec-list-table" style="width: 100%">
                        <el-table-column label="任务名称" min-width="88" align="left" header-align="left"
                            class-name="sec-name-col" label-class-name="sec-name-col">
                            <template slot-scope="{ row }">
                                <el-tooltip v-if="row.name" :content="row.name" placement="top"
                                    popper-class="sec-name-tooltip">
                                    <span class="sec-name">{{ row.name }}</span>
                                </el-tooltip>
                                <span v-else class="sec-name">—</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="stageName" label="学段" width="54" align="center"
                            class-name="sec-col-3chars" />
                        <el-table-column prop="subjectName" label="学科" width="72" align="center"
                            show-overflow-tooltip />
                        <el-table-column label="年级" min-width="88" align="center" show-overflow-tooltip>
                            <template slot-scope="{ row }">{{ formatGradeName(row) }}</template>
                        </el-table-column>
                        <el-table-column label="截止时间" width="100" align="center">
                            <template slot-scope="{ row }">
                                <span class="sec-deadline">{{ formatDeadline(row.deadline) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="选择空间" min-width="88" align="left">
                            <template slot-scope="{ row }">
                                <div v-if="validSpaces(row).length" class="sec-space-preview">
                                    <div class="sec-space-line">
                                        <span class="sec-tag sec-space-name">{{
                                            spaceLabel(previewSpaces(row)[0])
                                            }}</span>
                                    </div>
                                    <div v-if="validSpaces(row).length > 1" class="sec-space-line">
                                        <span class="sec-tag sec-space-name">{{
                                            spaceLabel(previewSpaces(row)[1])
                                            }}</span>
                                        <el-tooltip v-if="spaceMoreCount(row)" :content="spaceMoreNames(row)"
                                            placement="top">
                                            <span class="sec-tag sec-space-more">+{{ spaceMoreCount(row) }}</span>
                                        </el-tooltip>
                                    </div>
                                </div>
                                <span v-else class="sec-muted">—</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="提交份数" width="72" align="center">
                            <template slot-scope="{ row }">
                                <span class="sec-submit">{{ formatSubmit(row.submitCount) }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" align="center" width="128">
                            <template slot-scope="{ row }">
                                <div class="sec-actions">
                                    <el-button type="text" size="mini" @click="openDetail(row)">查看</el-button>
                                    <el-button type="text" size="mini"
                                        @click="openEdit(row)">编辑</el-button>
                                    <el-button type="text" size="mini" :disabled="row.status === 1"
                                        @click="openPublish(row)">{{ row.status === 1 ? '已发布' : '发布' }}</el-button>
                                    <el-button type="text" size="mini" class="sec-action-delete"
                                        @click="openDelete(row)">删除</el-button>
                                </div>
                            </template>
                        </el-table-column>
                        <template slot="empty">
                            <el-empty description="暂无组卷任务" />
                        </template>
                    </el-table>
                </div>
                <div v-show="total" class="sec-pagination">
                    <span class="sec-pagination-total">共 {{ total }} 条</span>
                    <pagination :total="total" :page.sync="page" :limit.sync="pageSize" :pageSizes="[10, 20, 30]"
                        @pagination="onPageChange" />
                </div>
            </div>

            <!-- 创建 / 编辑 -->
            <div v-if="view === 'form'" v-loading="detailLoading" class="sec-panel">
                <div class="sec-panel-header">
                    <el-button size="small" icon="el-icon-arrow-left" @click="backToList">返回列表</el-button>
                    <div class="sec-panel-title">{{ formMode === 'edit' ? '编辑组卷任务' : '创建组卷任务' }}</div>
                </div>
                <div class="sec-form-panel">
                    <div class="sec-section-title">基本信息</div>
                    <el-form :model="form" label-width="96px" size="small" @submit.native.prevent>
                        <el-form-item label="任务名称" required>
                            <el-input v-model.trim="form.name" maxlength="200" placeholder="如：高二数学期中考试试卷" />
                        </el-form-item>
                        <el-row :gutter="16">
                            <el-col :span="8">
                                <el-form-item label="学段" required>
                                    <el-select v-model="form.stage" placeholder="请选择" style="width: 100%"
                                        @change="onStageChange">
                                        <el-option v-for="item in stageOptions" :key="item.value" :label="item.label"
                                            :value="item.value" />
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :span="8">
                                <el-form-item label="学科" required>
                                    <el-select v-model="form.subjectId" :disabled="!form.stage"
                                        :placeholder="form.stage ? '请选择学科' : '请先选择学段'" style="width: 100%"
                                        @change="onSubjectChange">
                                        <el-option v-for="item in subjectOptions" :key="item.id" :label="item.name"
                                            :value="item.id" />
                                    </el-select>
                                </el-form-item>
                            </el-col>
                            <el-col :span="8">
                                <el-form-item label="年级">
                                    <el-select v-model="form.grade" multiple collapse-tags clearable
                                        :disabled="!form.stage"
                                        :placeholder="form.stage ? '请选择年级（可多选）' : '请先选择学段'"
                                        style="width: 100%"
                                        @change="onGradeChange">
                                        <el-option v-for="item in gradeOptions" :key="item.value" :label="item.label"
                                            :value="item.value" />
                                    </el-select>
                                </el-form-item>
                            </el-col>
                        </el-row>
                        <el-form-item label="选择空间" required>
                            <div class="sec-space-row">
                                <el-select v-model="form.spxIds" multiple filterable collapse-tags
                                    :disabled="!canSelectSpace" :loading="spaceLoading"
                                    :placeholder="canSelectSpace ? '请选择子空间' : '请先选择学段和学科'"
                                    style="width: 100%">
                                    <el-option-group v-for="group in spaceGroups" :key="group.key" :label="group.label">
                                        <el-option v-for="item in group.children" :key="item.spxId"
                                            :label="item.displayName || item.spxName" :value="item.spxId" />
                                    </el-option-group>
                                </el-select>
                                <el-button size="small" :disabled="!spaceOptions.length"
                                    @click="selectAllSpaces">全部</el-button>
                            </div>
                            <div v-if="!canSelectSpace" class="sec-hint">请先选择学段、学科，再选择匹配的子空间（可多选）</div>
                        </el-form-item>
                        <el-form-item label="截止时间" required>
                            <el-date-picker v-model="form.deadline" type="datetime"
                                value-format="yyyy-MM-dd HH:mm:ss" placeholder="请选择截止时间" style="width: 100%" />
                        </el-form-item>
                        <el-form-item label="细目表分类" required>
                            <div class="sec-spec-tabs" role="tablist" aria-label="细目表分类">
                                <button type="button" class="sec-spec-tab"
                                    :class="{ 'is-active': specMode === 0 }"
                                    @click="setSpecMode(0)">预置细目表</button>
                                <button type="button" class="sec-spec-tab"
                                    :class="{ 'is-active': specMode === 1 }"
                                    @click="setSpecMode(1)">自建细目表</button>
                            </div>
                            <div v-if="specMode === 1" class="sec-hint">自建细目表需上传模板，系统解析表头字段后再入库</div>

                            <!-- 预置细目表：固定表头 specMode=0 -->
                            <div v-show="specMode === 0" class="sec-spec-panel">
                                <div v-if="showMathTemplate" class="sec-import-head sec-import-head-end">
                                    <el-button type="text" icon="el-icon-download"
                                        @click="downloadMathTemplate">下载导入模板</el-button>
                                </div>
                                <el-upload v-show="!hasSpecReady" class="sec-upload" drag action=""
                                    :show-file-list="false" accept=".xlsx,.xls,.csv" :disabled="importing"
                                    :http-request="onImportSpec">
                                    <i class="el-icon-document sec-upload-icon"></i>
                                    <div class="el-upload__text">点击或拖拽整表文件到此处上传</div>
                                    <div class="sec-upload-hint">支持 .xlsx / .xls / .csv，将导入双向细目表全部行内容</div>
                                    <div class="sec-upload-hint">表头需包含：知识点名称、关键能力名称、学科能力名称、考察要求、测评目标（整表详细内容入库）</div>
                                </el-upload>
                                <div v-if="hasSpecReady" class="sec-file">
                                    <i class="el-icon-document"></i>
                                    <div class="sec-file-meta">
                                        <div class="sec-file-name">{{ specFileName || '双向细目表' }}</div>
                                        <div class="sec-file-status">已解析 {{ specs.length }} 行，保存后写入任务</div>
                                    </div>
                                    <el-upload action="" :show-file-list="false" accept=".xlsx,.xls,.csv"
                                        :disabled="importing" :http-request="onImportSpec">
                                        <el-button type="text" size="mini">重新导入</el-button>
                                    </el-upload>
                                    <el-button type="text" size="mini" @click="clearSpec()">移除</el-button>
                                </div>
                            </div>

                            <!-- 自建细目表：非固定表头 specMode=1 -->
                            <div v-show="specMode === 1" class="sec-spec-panel">
                                <div class="sec-import-head">
                                    <div class="sec-sub-label"><span class="sec-req">*</span>上传细目表模板</div>
                                    <el-button type="text" icon="el-icon-download"
                                        @click="downloadPhysicsTemplate">下载物理自建细目表模板</el-button>
                                </div>
                                <el-upload v-show="!hasSpecReady" class="sec-upload" drag action=""
                                    :show-file-list="false" accept=".xlsx,.xls,.csv" :disabled="importing"
                                    :http-request="onImportSpec">
                                    <i class="el-icon-document sec-upload-icon"></i>
                                    <div class="el-upload__text">点击或拖拽模板文件到此处上传</div>
                                    <div class="sec-upload-hint">推荐使用「物理自建细目表模板」（.xlsx）；亦支持 .xls /
                                        .csv，上传后自动解析表头字段</div>
                                    <div class="sec-upload-hint">{{ physicsTemplateHint }}</div>
                                </el-upload>
                                <div v-if="hasSpecReady" class="sec-file">
                                    <i class="el-icon-document"></i>
                                    <div class="sec-file-meta">
                                        <div class="sec-file-name">{{ specFileName || '细目表模板' }}</div>
                                        <div class="sec-file-status">模板解析成功 · {{ specColumns.length }} 个字段 ·
                                            {{ specs.length }} 行</div>
                                    </div>
                                    <el-upload action="" :show-file-list="false" accept=".xlsx,.xls,.csv"
                                        :disabled="importing" :http-request="onImportSpec">
                                        <el-button type="text" size="mini">重新导入</el-button>
                                    </el-upload>
                                    <el-button type="text" size="mini" @click="clearSpec()">移除</el-button>
                                </div>
                                <div v-if="specColumns.length" class="sec-parsed-fields">
                                    <div class="sec-parsed-label">
                                        解析字段
                                        <span class="sec-parsed-count">共 {{ specColumns.length }} 个</span>
                                    </div>
                                    <div class="sec-parsed-list">
                                        <span v-for="(col, idx) in specColumns" :key="'c-' + col.columnName + idx"
                                            class="sec-parsed-tag">
                                            <span class="sec-parsed-idx">{{ idx + 1 }}</span>{{ col.columnName }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="hasSpecReady && (specMode === 0 ? specs.length : true)" class="sec-preview">
                                <div class="sec-preview-hd">
                                    <span>双向细目表整表预览</span>
                                    <span class="sec-muted">{{ previewMetaText }}</span>
                                </div>
                                <!-- 固定表头 -->
                                <el-table v-if="specMode === 0" :data="specs" size="mini" max-height="320" border>
                                    <el-table-column prop="questionNo" label="题号" width="72" />
                                    <el-table-column prop="questionType" label="题型" width="88"
                                        show-overflow-tooltip />
                                    <el-table-column prop="knowledgeName" label="知识点名称" min-width="120"
                                        show-overflow-tooltip />
                                    <el-table-column prop="keyAbilityName" label="关键能力名称" min-width="120"
                                        show-overflow-tooltip />
                                    <el-table-column prop="subjectAbilityName" label="学科能力名称" min-width="120"
                                        show-overflow-tooltip />
                                    <el-table-column prop="requirement" label="考察要求" width="96"
                                        show-overflow-tooltip />
                                    <el-table-column prop="objective" label="测评目标" width="96"
                                        show-overflow-tooltip />
                                </el-table>
                                <!-- 非固定表头 -->
                                <el-table v-else :data="customPreviewRows" size="mini" max-height="320" border>
                                    <el-table-column prop="questionNo" label="题号" width="72" />
                                    <el-table-column v-for="col in previewColumns" :key="col.columnName"
                                        :prop="col.columnName" :label="col.columnName" min-width="110"
                                        show-overflow-tooltip />
                                    <template slot="empty">
                                        <el-empty description="已解析表头，暂无数据行" :image-size="64" />
                                    </template>
                                </el-table>
                                <div class="sec-tags sec-dim-tags">
                                    <span v-for="item in formDimensionStats" :key="item.label"
                                        class="sec-tag">{{ item.label }} {{ item.count }}</span>
                                </div>
                            </div>
                        </el-form-item>

                        <div v-if="specMode === 0" class="sec-goal">
                            <div class="sec-goal-hd">
                                <div class="sec-goal-title"><span class="sec-req">*</span>任务目标</div>
                            </div>
                            <div class="sec-goal-desc">填写课本任务要检测的知识与能力、考查重点与预期产出，至少填写「总体目标」后方可保存。</div>
                            <div v-for="field in goalFields" :key="field.key" class="sec-goal-field">
                                <div class="sec-goal-label">
                                    <span v-if="field.required" class="sec-req">*</span>{{ field.label }}
                                </div>
                                <div class="sec-goal-hint">{{ field.hint }}</div>
                                <el-input v-model="form.goal[field.key]" type="textarea" :rows="3"
                                    :maxlength="field.max" show-word-limit resize="none"
                                    :placeholder="field.placeholder" />
                            </div>
                        </div>
                    </el-form>
                    <div class="sec-footer">
                        <el-button size="small" @click="backToList">取消</el-button>
                        <el-button type="primary" size="small" :loading="saving" @click="saveTask">保存</el-button>
                    </div>
                </div>
            </div>

            <!-- 查看 -->
            <div v-if="view === 'detail'" v-loading="detailLoading" class="sec-panel">
                <div class="sec-panel-header">
                    <el-button size="small" icon="el-icon-arrow-left" @click="backToList">返回列表</el-button>
                    <div class="sec-panel-title">查看组卷任务</div>
                </div>
                <div class="sec-form-panel">
                    <div class="sec-section-title">基本信息</div>
                    <el-descriptions :column="2" border size="small">
                        <el-descriptions-item label="任务名称" :span="2">{{ detail.name || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="学段">{{ detail.stageName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="学科">{{ detail.subjectName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="年级">{{ formatGradeName(detail) }}</el-descriptions-item>
                        <el-descriptions-item label="截止时间">{{ detail.deadline || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="提交份数">{{ formatSubmit(detailSubmitCount) }}</el-descriptions-item>
                        <el-descriptions-item label="发布状态">{{ detail.statusName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="选择空间" :span="2">
                            <div class="sec-tags">
                                <span v-for="space in detail.spaces || []" :key="space.spxId" class="sec-tag">{{
                                    space.displayName || space.spxName }}</span>
                                <span v-if="!(detail.spaces && detail.spaces.length)" class="sec-muted">—</span>
                            </div>
                        </el-descriptions-item>
                    </el-descriptions>

                    <div class="sec-section-title sec-section-gap">双向细目表</div>
                    <div class="sec-hint" style="margin-bottom: 8px">分类：{{ detailSpecModeLabel }}</div>
                    <div class="sec-file">
                        <i class="el-icon-document"></i>
                        <div class="sec-file-meta">
                            <div class="sec-file-name">{{ detail.specFileName || '双向细目表' }}</div>
                            <div class="sec-file-status">{{ detailSpecStatusText }}</div>
                        </div>
                    </div>
                    <!-- 查看回显：仅展示表头，不展示明细行 -->
                    <el-table v-if="detailSpecMode === 0" :data="[]" size="mini" border class="sec-spec-header-only"
                        style="margin-top: 12px">
                        <el-table-column prop="questionNo" label="题号" width="72" />
                        <el-table-column prop="questionType" label="题型" width="88" show-overflow-tooltip />
                        <el-table-column prop="knowledgeName" label="知识点名称" min-width="120" show-overflow-tooltip />
                        <el-table-column prop="keyAbilityName" label="关键能力名称" min-width="120" show-overflow-tooltip />
                        <el-table-column prop="subjectAbilityName" label="学科能力名称" min-width="120"
                            show-overflow-tooltip />
                        <el-table-column prop="requirement" label="考察要求" width="96" show-overflow-tooltip />
                        <el-table-column prop="objective" label="测评目标" width="96" show-overflow-tooltip />
                    </el-table>
                    <el-table v-else :data="[]" size="mini" border class="sec-spec-header-only"
                        style="margin-top: 12px">
                        <el-table-column prop="questionNo" label="题号" width="72" />
                        <el-table-column v-for="col in detailPreviewColumns" :key="'d-' + col.columnName"
                            :prop="col.columnName" :label="col.columnName" min-width="110" show-overflow-tooltip />
                        <el-table-column prop="creatorUserName" label="填写人" width="96" show-overflow-tooltip />
                        <template slot="empty">
                            <el-empty description="暂无细目表" />
                        </template>
                    </el-table>
                    <div class="sec-tags sec-dim-tags">
                        <span v-for="item in detailDimensionStats" :key="item.label" class="sec-tag">{{
                            item.label }} {{ item.count }}</span>
                    </div>
                    <div v-if="detailSpecMode === 0" class="sec-goal sec-goal-view">
                        <div class="sec-goal-hd">
                            <div class="sec-goal-title">任务目标</div>
                        </div>
                        <div v-for="field in goalFields" :key="'dg-' + field.key" class="sec-goal-field">
                            <div class="sec-goal-label">{{ field.label }}</div>
                            <div class="sec-goal-text">{{ detailGoalText(field.key) }}</div>
                        </div>
                    </div>
                    <div class="sec-footer">
                        <el-button size="small" @click="backToList">关闭</el-button>
                        <el-button type="primary" size="small"
                            @click="openEdit(detail)">去编辑</el-button>
                    </div>
                </div>
            </div>
        </div>

        <el-dialog title="确认发布" :visible.sync="publishVisible" width="420px" append-to-body
            custom-class="sec-confirm-dialog">
            <div class="sec-confirm">确认发布组卷任务「<strong>{{ pendingPublish && pendingPublish.name
            }}</strong>」吗？发布后将下发给所选空间，空间成员可预览双向细目表。</div>
            <span slot="footer">
                <el-button size="small" @click="publishVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="publishing" @click="confirmPublish">确认发布</el-button>
            </span>
        </el-dialog>

        <el-dialog title="确认删除" :visible.sync="deleteVisible" width="420px" append-to-body
            custom-class="sec-confirm-dialog">
            <div class="sec-confirm">确认删除组卷任务「<strong>{{ pendingDelete && pendingDelete.name }}</strong>」吗？删除后不可恢复。
            </div>
            <span slot="footer">
                <el-button size="small" @click="deleteVisible = false">取消</el-button>
                <el-button type="primary" size="small" :loading="deleting" @click="confirmDelete">确认删除</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import pagination from '@/common/community/pagination.vue';

    const STAGE_OPTIONS = [
        { value: 1, label: '小学' },
        { value: 2, label: '初中' },
        { value: 3, label: '九年一贯制' },
        { value: 4, label: '高中' },
        // { value: 5, label: '幼儿园' },
        // { value: 6, label: '完全中学' },
        { value: 7, label: '十二年一贯制' },
        // { value: 8, label: '大学' },
        // { value: 9, label: '其他' },
        // { value: 10, label: '特殊教育学校' },
        // { value: 11, label: '中等职业教育' },
    ];
    const SUBJECT_OPTIONS = [
        { id: 1, name: '语文' },
        { id: 2, name: '数学' },
        { id: 3, name: '英语' },
        { id: 4, name: '物理' },
        { id: 5, name: '化学' },
        { id: 6, name: '生物学' },
        { id: 7, name: '政治（道法）' },
        { id: 8, name: '历史' },
        { id: 9, name: '地理' },
        { id: 10, name: '科学' },
        { id: 11, name: '历史与社会' },
        { id: 12, name: '信息技术' },
        { id: 13, name: '音乐' },
        { id: 14, name: '美术' },
        { id: 15, name: '体育与健康' },
        { id: 16, name: '通用技术' },
        { id: 17, name: '劳动技术' },
        { id: 18, name: '拓展' },
        { id: 19, name: '心理健康' },
        { id: 20, name: '综合实践活动' },
        { id: 21, name: '日语' },
        { id: 22, name: '俄语' },
        { id: 23, name: '书法' },
        { id: 24, name: '社会' },
        { id: 25, name: '职教专业课' },
        { id: 26, name: '藏语' },
        { id: 27, name: '西班牙语' },
        { id: 28, name: '德语' },
        { id: 29, name: '法语' },
        { id: 30, name: '地方课程' },
    ];
    const GRADE_MAP = {
        1: [
            { value: 1, label: '一年级' },
            { value: 2, label: '二年级' },
            { value: 3, label: '三年级' },
            { value: 4, label: '四年级' },
            { value: 5, label: '五年级' },
            { value: 6, label: '六年级' },
        ],
        2: [
            { value: 7, label: '初一' },
            { value: 8, label: '初二' },
            { value: 9, label: '初三' },
        ],
        3: [
            { value: 1, label: '一年级' },
            { value: 2, label: '二年级' },
            { value: 3, label: '三年级' },
            { value: 4, label: '四年级' },
            { value: 5, label: '五年级' },
            { value: 6, label: '六年级' },
            { value: 7, label: '初一' },
            { value: 8, label: '初二' },
            { value: 9, label: '初三' },
        ],
        4: [
            { value: 10, label: '高一' },
            { value: 11, label: '高二' },
            { value: 12, label: '高三' },
        ],
        7: [
            { value: 1, label: '一年级' },
            { value: 2, label: '二年级' },
            { value: 3, label: '三年级' },
            { value: 4, label: '四年级' },
            { value: 5, label: '五年级' },
            { value: 6, label: '六年级' },
            { value: 7, label: '初一' },
            { value: 8, label: '初二' },
            { value: 9, label: '初三' },
            { value: 10, label: '高一' },
            { value: 11, label: '高二' },
            { value: 12, label: '高三' },
        ],
    };
    const DIM_FIELDS = [
        { key: 'knowledgeName', label: '知识点名称' },
        { key: 'keyAbilityName', label: '关键能力名称' },
        { key: 'subjectAbilityName', label: '学科能力名称' },
        { key: 'requirement', label: '考察要求' },
        { key: 'objective', label: '测评目标' },
    ];
    const PHYSICS_TEMPLATE_HEADERS = [
        '题号',
        '题型',
        '知识细目（具体考查的知识内容）',
        '能力细目（关键能力名称）',
        '3*2学科能力名称',
        '学业质量水平',
        '分值',
        '预计得分率',
        '实际得分率',
    ];
    /** 规范为 0 固定表头 / 1 非固定表头 */
    const normalizeSpecMode = (value) => {
        if (value === 1 || value === '1' || value === 'custom') return 1;
        if (value === 0 || value === '0' || value === 'preset') return 0;
        return 0;
    };
    const mapFixedSpecRow = (item) => ({
        questionNo: item.questionNo,
        questionType: item.questionType,
        knowledgeName: item.knowledgeName,
        keyAbilityName: item.keyAbilityName,
        subjectAbilityName: item.subjectAbilityName,
        requirement: item.requirement,
        objective: item.objective,
    });
    const mapCustomSpecRow = (item) => ({
        questionNo: item.questionNo != null ? String(item.questionNo) : '',
        cells: item.cells && typeof item.cells === 'object' ? { ...item.cells } : {},
        creatorUserId: item.creatorUserId,
        creatorUserName: item.creatorUserName,
        sort: item.sort,
    });
    const mapSpecColumn = (item, index) => ({
        sort: item.sort != null ? item.sort : index,
        columnName: item.columnName || '',
        builtinKey: item.builtinKey != null ? item.builtinKey : null,
        valueType: item.valueType || 'text',
        options: Array.isArray(item.options) ? item.options.slice() : null,
    });
    const buildCustomPreviewRows = (specs, columns) => {
        const cols = columns || [];
        return (specs || []).map((row) => {
            const out = {
                questionNo: row.questionNo || '',
                creatorUserName: row.creatorUserName || '',
            };
            cols.forEach((col) => {
                const name = col.columnName;
                if (!name || name === '题号') return;
                out[name] = row.cells && row.cells[name] != null ? row.cells[name] : '';
            });
            return out;
        });
    };
    const customDimensionStats = (columns, specs) => {
        return (columns || [])
            .filter((col) => col.columnName && col.columnName !== '题号')
            .map((col) => {
                const set = {};
                (specs || []).forEach((row) => {
                    const value = row && row.cells && row.cells[col.columnName] != null
                        ? String(row.cells[col.columnName]).trim()
                        : '';
                    if (value) set[value] = true;
                });
                return { label: col.columnName, count: Object.keys(set).length };
            });
    };

    const unwrapApi = (res) => {
        if (!res) return res;
        if (res.status != null && res.data && typeof res.data === 'object' && 'code' in res.data) {
            return res.data;
        }
        return res;
    };
    const isApiOk = (res) => {
        const body = unwrapApi(res);
        return body && (body.code === 0 || body.code === 200);
    };
    const apiMsg = (res) => {
        const body = unwrapApi(res);
        return (body && body.msg) || '';
    };
    const apiData = (res) => {
        const body = unwrapApi(res);
        return body && body.data;
    };

    const GOAL_FIELDS = [
        {
            key: 'overall',
            label: '总体目标',
            required: true,
            max: 1000,
            hint: '必填，根据本任务要达成的命题目的',
            placeholder: '例如：围绕函数与不等式核心内容，出一套可诊断高二学生基础掌握与简单应用能力的专项练习卷，供专家审阅后用于阶段巩固。',
        },
        {
            key: 'knowledge',
            label: '知识目标',
            required: false,
            max: 800,
            hint: '可选，列出拟覆盖的知识点和章节',
            placeholder: '例如：函数概念与性质、等式与不等式的性质、充分必要条件。',
        },
        {
            key: 'ability',
            label: '能力目标',
            required: false,
            max: 800,
            hint: '可选，如运算求解、逻辑思维、直观想象等',
            placeholder: '例如：运算求解能力、逻辑思维能力；能用函数与不等式表达并解决简单实际问题。',
        },
        {
            key: 'focus',
            label: '考查重点与预期产出',
            required: false,
            max: 800,
            hint: '可选，说明难度梯度、题量结构或题型要求',
            placeholder: '例如：基础题约 60%、应用题约 30%、综合题约 10%；题量约 20–25 题，覆盖选择题、填空题与解答题。',
        },
    ];

    /** 详情回显兼容：单值 / 数组 / 逗号串 → number[] */
    const normalizeGrades = (raw) => {
        if (raw == null || raw === '') return [];
        if (Array.isArray(raw)) {
            return raw.map((item) => Number(item)).filter((n) => !Number.isNaN(n));
        }
        if (typeof raw === 'string' && raw.indexOf(',') >= 0) {
            return raw.split(',').map((s) => Number(String(s).trim())).filter((n) => !Number.isNaN(n));
        }
        const n = Number(raw);
        return Number.isNaN(n) ? [] : [n];
    };

    /** spaces：一个 "12"，多个 "10,11,12"，未选 "" */
    const formatGradeParam = (raw) => normalizeGrades(raw).join(',');

    const emptyGoal = () => ({
        overall: '',
        knowledge: '',
        ability: '',
        focus: '',
    });

    const emptyForm = () => ({
        id: null,
        name: '',
        stage: '',
        subjectId: '',
        grade: [],
        deadline: '',
        spxIds: [],
        goal: emptyGoal(),
    });

    export default {
        name: 'SpaceExamCompose',
        components: { pagination },
        data() {
            return {
                view: 'list',
                formMode: 'create',
                stageOptions: STAGE_OPTIONS,
                subjectOptions: SUBJECT_OPTIONS,
                keyword: '',
                subjectId: '',
                records: [],
                total: 0,
                page: 1,
                pageSize: 10,
                listLoading: false,
                searchTimer: null,
                form: emptyForm(),
                goalFields: GOAL_FIELDS,
                /** 0 固定表头（预置） / 1 非固定表头（自建） */
                specMode: 0,
                specs: [],
                specColumns: [],
                specFileName: '',
                spaceOptions: [],
                spaceLoading: false,
                importing: false,
                saving: false,
                hydrating: false,
                detail: {},
                detailSubmitCount: null,
                detailLoading: false,
                publishVisible: false,
                pendingPublish: null,
                publishing: false,
                deleteVisible: false,
                pendingDelete: null,
                deleting: false,
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            gradeOptions() {
                return GRADE_MAP[this.form.stage] || [];
            },
            canSelectSpace() {
                return !!(this.form.stage && this.form.subjectId);
            },
            spaceGroups() {
                const map = {};
                const order = [];
                this.spaceOptions.forEach((item) => {
                    const key = item.parentId != null ? String(item.parentId) : (item.parentName || 'other');
                    if (!map[key]) {
                        map[key] = {
                            key,
                            label: item.parentName || '未分组',
                            children: [],
                        };
                        order.push(map[key]);
                    }
                    map[key].children.push(item);
                });
                return order;
            },
            showMathTemplate() {
                return Number(this.form.stage) === 4 && Number(this.form.subjectId) === 2;
            },
            physicsTemplateHint() {
                return '物理自建模板表头：' + PHYSICS_TEMPLATE_HEADERS.join('、');
            },
            /** 预置需有行；自建有表头即可（允许仅表头） */
            hasSpecReady() {
                if (this.specMode === 1) return this.specColumns.length > 0;
                return this.specs.length > 0;
            },
            previewColumns() {
                return (this.specColumns || []).filter((col) => col.columnName && col.columnName !== '题号');
            },
            customPreviewRows() {
                return buildCustomPreviewRows(this.specs, this.specColumns);
            },
            previewMetaText() {
                if (this.specMode === 1) {
                    return '已解析 ' + this.specs.length + ' 行 · ' + this.specColumns.length + ' 个字段';
                }
                return '已解析 ' + this.specs.length + ' 行 · 5 类维度';
            },
            formDimensionStats() {
                if (this.specMode === 1) return customDimensionStats(this.specColumns, this.specs);
                return this.dimensionStats(this.specs);
            },
            detailSpecMode() {
                return normalizeSpecMode(this.detail && this.detail.specMode);
            },
            detailSpecModeLabel() {
                return this.detailSpecMode === 1 ? '自建细目表（非固定表头）' : '预置细目表（固定表头）';
            },
            detailSpecStatusText() {
                if (this.detailSpecMode === 1) {
                    const cols = (this.detail.specColumns || []).length;
                    const rows = (this.detail.specs || []).length;
                    return '非固定表头已入库 · ' + cols + ' 列 · ' + rows + ' 行';
                }
                return '整表已入库 · 知识点 / 关键能力 / 学科能力 / 考察要求 / 测评目标';
            },
            detailPreviewColumns() {
                return (this.detail.specColumns || [])
                    .map(mapSpecColumn)
                    .filter((col) => col.columnName && col.columnName !== '题号');
            },
            detailCustomPreviewRows() {
                const specs = (this.detail.specs || []).map(mapCustomSpecRow);
                return buildCustomPreviewRows(specs, this.detail.specColumns || []);
            },
            detailDimensionStats() {
                if (this.detailSpecMode === 1) {
                    return customDimensionStats(this.detail.specColumns || [], (this.detail.specs || []).map(mapCustomSpecRow));
                }
                return this.dimensionStats(this.detail.specs || []);
            },
        },
        watch: {
            projectId() {
                this.view = 'list';
                this.page = 1;
                this.fetchList();
            },
        },
        mounted() {
            this.fetchList();
        },
        beforeDestroy() {
            clearTimeout(this.searchTimer);
        },
        methods: {
            formatGradeName(row) {
                const source = row && typeof row === 'object' ? row : { gradeName: row };
                const raw = source.gradeName != null && source.gradeName !== '' ? source.gradeName : source.grade;
                const names = this.parseGradeNames(raw);
                return names.length ? names.join('、') : '—';
            },
            parseGradeNames(raw) {
                if (raw == null || raw === '') return [];
                let list = raw;
                if (typeof raw === 'string') {
                    const text = raw.trim();
                    if (!text) return [];
                    if (text.charAt(0) === '[') {
                        try {
                            const parsed = JSON.parse(text);
                            list = Array.isArray(parsed) ? parsed : [text];
                        } catch (e) {
                            list = text.split(',');
                        }
                    } else if (text.indexOf(',') >= 0) {
                        list = text.split(',');
                    } else {
                        list = [text];
                    }
                } else if (!Array.isArray(list)) {
                    list = [list];
                }
                const labels = {};
                Object.keys(GRADE_MAP).forEach((key) => {
                    (GRADE_MAP[key] || []).forEach((item) => {
                        labels[String(item.value)] = item.label;
                    });
                });
                return list.map((item) => {
                    const text = String(item == null ? '' : item).trim();
                    if (!text) return '';
                    return labels[text] || text;
                }).filter(Boolean);
            },
            formatDeadline(value) {
                if (!value) return '—';
                return String(value).slice(0, 10);
            },
            formatSubmit(count) {
                if (count === 0 || count) return count + '份';
                return '—';
            },
            spaceLabel(space) {
                if (!space) return '';
                return space.displayName || space.spxName || '';
            },
            validSpaces(row) {
                return ((row && row.spaces) || []).filter((space) => {
                    const name = space && space.spxName;
                    return name != null && String(name).trim() !== '';
                });
            },
            previewSpaces(row) {
                return this.validSpaces(row).slice(0, 2);
            },
            spaceMoreCount(row) {
                const total = this.validSpaces(row).length;
                return total > 2 ? total - 2 : 0;
            },
            spaceMoreNames(row) {
                return this.validSpaces(row)
                    .slice(2)
                    .map((space) => this.spaceLabel(space))
                    .filter(Boolean)
                    .join('、');
            },
            dimensionStats(rows) {
                return DIM_FIELDS.map((field) => {
                    const set = {};
                    (rows || []).forEach((row) => {
                        const value = row && row[field.key] != null ? String(row[field.key]).trim() : '';
                        if (value) set[value] = true;
                    });
                    return { label: field.label, count: Object.keys(set).length };
                });
            },
            onKeywordInput() {
                clearTimeout(this.searchTimer);
                this.searchTimer = setTimeout(() => {
                    this.onFilterChange();
                }, 300);
            },
            onFilterChange() {
                this.page = 1;
                this.fetchList();
            },
            onPageChange({ pageIndex, pageSize } = {}) {
                if (pageSize) this.pageSize = pageSize;
                this.page = pageIndex || 1;
                this.fetchList();
            },
            async fetchList() {
                if (!this.projectId) return;
                this.listLoading = true;
                try {
                    const params = {
                        projectId: this.projectId,
                        page: this.page,
                        pageSize: this.pageSize,
                    };
                    if (this.keyword) params.name = this.keyword;
                    if (this.subjectId) params.subjectId = this.subjectId;
                    const res = await this.$Api.ExamCompose.getList(params);
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '列表加载失败');
                        this.records = [];
                        this.total = 0;
                        return;
                    }
                    const data = apiData(res) || {};
                    this.records = data.records || [];
                    this.total = data.totalCount || 0;
                } catch (e) {
                    this.records = [];
                    this.total = 0;
                    this.$message.error('列表加载失败');
                } finally {
                    this.listLoading = false;
                }
            },
            backToList() {
                this.view = 'list';
                this.fetchList();
            },
            openCreate() {
                this.formMode = 'create';
                this.form = emptyForm();
                this.specMode = 0;
                this.specs = [];
                this.specColumns = [];
                this.specFileName = '';
                this.spaceOptions = [];
                this.view = 'form';
            },
            async openEdit(row) {
                if (!row || !row.id) return;
                this.formMode = 'edit';
                this.view = 'form';
                await this.fillForm(row.id);
            },
            async openDetail(row) {
                this.detailSubmitCount = row ? row.submitCount : null;
                this.view = 'detail';
                this.detailLoading = true;
                try {
                    const res = await this.$Api.ExamCompose.getDetail({ id: row.id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '详情加载失败');
                        this.detail = {};
                        return;
                    }
                    this.detail = apiData(res) || {};
                } catch (e) {
                    this.detail = {};
                    this.$message.error('详情加载失败');
                } finally {
                    this.detailLoading = false;
                }
            },
            async fillForm(id) {
                this.detailLoading = true;
                this.hydrating = true;
                try {
                    const res = await this.$Api.ExamCompose.getDetail({ id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '详情加载失败');
                        this.backToList();
                        return;
                    }
                    const detail = apiData(res) || {};
                    this.form = {
                        id: detail.id,
                        name: detail.name || '',
                        stage: detail.stage || '',
                        subjectId: detail.subjectId || '',
                        grade: normalizeGrades(detail.grades != null ? detail.grades : detail.grade),
                        deadline: detail.deadline || '',
                        spxIds: (detail.spaces || []).map((item) => item.spxId),
                        goal: Object.assign(emptyGoal(), (detail && detail.goal) || {}),
                    };
                    this.applyDetailSpec(detail);
                    await this.loadSpaces();
                    this.mergeSelectedSpaces(detail.spaces);
                } catch (e) {
                    this.$message.error('详情加载失败');
                    this.backToList();
                } finally {
                    this.hydrating = false;
                    this.detailLoading = false;
                }
            },
            onStageChange() {
                if (this.hydrating) return;
                this.form.subjectId = '';
                this.form.grade = [];
                this.form.spxIds = [];
                this.spaceOptions = [];
            },
            onSubjectChange() {
                if (this.hydrating) return;
                this.form.spxIds = [];
                this.spaceOptions = [];
                if (this.canSelectSpace) this.loadSpaces();
            },
            onGradeChange() {
                if (this.hydrating) return;
                this.form.spxIds = [];
                this.spaceOptions = [];
                if (this.canSelectSpace) this.loadSpaces();
            },
            async loadSpaces() {
                if (!this.projectId || !this.form.stage || !this.form.subjectId) {
                    this.spaceOptions = [];
                    return;
                }
                this.spaceLoading = true;
                try {
                    const params = {
                        projectId: this.projectId,
                        stage: this.form.stage,
                        subjectId: this.form.subjectId,
                    };
                    const grade = formatGradeParam(this.form.grade);
                    if (grade) params.grade = grade;
                    const res = await this.$Api.ExamCompose.getSpaces(params);
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '空间加载失败');
                        this.spaceOptions = [];
                        return;
                    }
                    const list = apiData(res);
                    this.spaceOptions = Array.isArray(list) ? list : [];
                } catch (e) {
                    this.spaceOptions = [];
                    this.$message.error('空间加载失败');
                } finally {
                    this.spaceLoading = false;
                }
            },
            mergeSelectedSpaces(spaces) {
                const map = {};
                this.spaceOptions.forEach((item) => {
                    map[item.spxId] = true;
                });
                (spaces || []).forEach((item) => {
                    if (item && item.spxId != null && !map[item.spxId]) {
                        this.spaceOptions.push(item);
                        map[item.spxId] = true;
                    }
                });
            },
            selectAllSpaces() {
                this.form.spxIds = this.spaceOptions.map((item) => item.spxId);
            },
            setSpecMode(mode) {
                const next = normalizeSpecMode(mode);
                if (this.specMode === next) return;
                this.specMode = next;
                this.clearSpec();
            },
            applyDetailSpec(detail) {
                const mode = normalizeSpecMode(detail && detail.specMode);
                this.specMode = mode;
                this.specFileName = (detail && detail.specFileName) || '';
                if (mode === 1) {
                    this.specColumns = (detail.specColumns || []).map(mapSpecColumn);
                    this.specs = (detail.specs || []).map(mapCustomSpecRow);
                } else {
                    this.specColumns = [];
                    this.specs = (detail.specs || []).map(mapFixedSpecRow);
                }
            },
            applyImportResult(data, fallbackName) {
                const mode = normalizeSpecMode(data && data.specMode != null ? data.specMode : this.specMode);
                this.specMode = mode;
                this.specFileName = (data && data.fileName) || fallbackName || '';
                if (mode === 1) {
                    this.specColumns = (data.specColumns || []).map(mapSpecColumn);
                    this.specs = (data.specs || []).map(mapCustomSpecRow);
                } else {
                    this.specColumns = [];
                    this.specs = (data.specs || []).map(mapFixedSpecRow);
                }
            },
            async downloadMathTemplate() {
                const href = (process.env.BASE_URL || '/') + 'templates/math.xlsx';
                try {
                    const res = await fetch(href);
                    if (!res.ok) {
                        this.$message.error('模板下载失败');
                        return;
                    }
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '高一2025—2026学年度第一学期学业水平调研（一）-数学试题标签.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
                } catch (e) {
                    this.$message.error('模板下载失败');
                }
            },
            async downloadPhysicsTemplate() {
                const href = (process.env.BASE_URL || '/') + 'templates/' + encodeURIComponent('物理细目表.xlsx');
                try {
                    const res = await fetch(href, { cache: 'no-store' });
                    if (!res.ok) {
                        this.$message.error('模板下载失败');
                        return;
                    }
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = '物理细目表.xlsx';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.setTimeout(() => window.URL.revokeObjectURL(url), 2000);
                    this.$message.success('已下载物理自建细目表模板');
                } catch (e) {
                    this.$message.error('模板下载失败');
                }
            },
            async onImportSpec({ file }) {
                const name = (file && file.name) || '';
                if (!/\.(xlsx|xls|csv)$/i.test(name)) {
                    this.$message.error('请上传 .xlsx / .xls / .csv 文件');
                    return;
                }
                this.importing = true;
                try {
                    // 预置不传 specMode（后端自动判断）；自建传 specMode=1
                    const form = { file };
                    if (this.specMode === 1) form.specMode = 1;
                    const res = await this.$Api.ExamCompose.importSpec(form);
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '导入失败');
                        return;
                    }
                    const data = apiData(res) || {};
                    this.applyImportResult(data, name);
                    const rowCount = data.rowCount != null ? data.rowCount : this.specs.length;
                    if (this.specMode === 1) {
                        this.$message.success(
                            '模板解析成功 · ' + this.specColumns.length + ' 个字段 · ' + rowCount + ' 行'
                        );
                    } else {
                        this.$message.success('已解析 ' + rowCount + ' 行');
                    }
                } catch (e) {
                    this.$message.error('导入失败');
                } finally {
                    this.importing = false;
                }
            },
            detailGoalText(key) {
                const goal = (this.detail && this.detail.goal) || {};
                const text = String(goal[key] || '').trim();
                return text || '—';
            },
            clearSpec() {
                this.specs = [];
                this.specColumns = [];
                this.specFileName = '';
            },
            async saveTask() {
                if (!this.projectId) {
                    this.$message.warning('请先进入项目');
                    return;
                }
                const name = (this.form.name || '').trim();
                if (!name) {
                    this.$message.warning('请填写任务名称');
                    return;
                }
                if (!this.form.stage) {
                    this.$message.warning('请选择学段');
                    return;
                }
                if (!this.form.subjectId) {
                    this.$message.warning('请选择学科');
                    return;
                }
                if (!this.form.spxIds.length) {
                    this.$message.warning('请选择空间');
                    return;
                }
                if (!this.form.deadline) {
                    this.$message.warning('请选择截止时间');
                    return;
                }
                if (this.specMode === 1) {
                    if (!this.specColumns.length) {
                        this.$message.warning('请先上传自建细目表模板');
                        return;
                    }
                } else if (!this.specs.length) {
                    this.$message.warning('请导入双向细目表');
                    return;
                }
                let goalPayload = null;
                if (this.specMode !== 1) {
                    const goal = emptyGoal();
                    GOAL_FIELDS.forEach((field) => {
                        goal[field.key] = String((this.form.goal && this.form.goal[field.key]) || '').trim();
                    });
                    if (!goal.overall) {
                        this.$message.warning('请填写任务目标中的总体目标');
                        return;
                    }
                    goalPayload = goal;
                }
                const payload = {
                    projectId: this.projectId,
                    name,
                    stage: this.form.stage,
                    subjectId: this.form.subjectId,
                    grade: normalizeGrades(this.form.grade),
                    deadline: this.form.deadline,
                    spxIds: this.form.spxIds,
                    specFileName: this.specFileName,
                };
                if (this.specMode === 1) {
                    // 自建不传 goal，目标由学员填写
                    payload.specMode = 1;
                    payload.specColumns = this.specColumns.map((col, index) => ({
                        sort: col.sort != null ? col.sort : index,
                        columnName: col.columnName,
                        builtinKey: col.builtinKey,
                        valueType: col.valueType || 'text',
                        options: col.options,
                    }));
                    payload.specs = this.specs.map((item) => ({
                        questionNo: item.questionNo || '',
                        cells: item.cells || {},
                    }));
                } else {
                    payload.specMode = 0;
                    payload.specs = this.specs.map(mapFixedSpecRow);
                    payload.goal = goalPayload;
                }
                if (this.formMode === 'edit' && this.form.id) payload.id = this.form.id;
                this.saving = true;
                try {
                    const res = await this.$Api.ExamCompose.save(payload);
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '保存失败');
                        return;
                    }
                    this.$message.success(this.formMode === 'edit' ? '组卷任务已更新' : '组卷任务已保存，可在列表中发布');
                    this.backToList();
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.saving = false;
                }
            },
            openPublish(row) {
                if (!row || row.status === 1) return;
                this.pendingPublish = row;
                this.publishVisible = true;
            },
            async confirmPublish() {
                if (!this.pendingPublish) return;
                this.publishing = true;
                try {
                    const res = await this.$Api.ExamCompose.publish({ id: this.pendingPublish.id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '发布失败');
                        return;
                    }
                    this.$message.success('已发布「' + this.pendingPublish.name + '」，空间成员可预览细目表');
                    this.publishVisible = false;
                    this.pendingPublish = null;
                    this.fetchList();
                } catch (e) {
                    this.$message.error('发布失败');
                } finally {
                    this.publishing = false;
                }
            },
            openDelete(row) {
                this.pendingDelete = row;
                this.deleteVisible = true;
            },
            async confirmDelete() {
                if (!this.pendingDelete || !this.pendingDelete.id) return;
                const name = this.pendingDelete.name;
                this.deleting = true;
                try {
                    const res = await this.$Api.ExamCompose.delete({ id: this.pendingDelete.id });
                    if (!isApiOk(res)) {
                        this.$message.error(apiMsg(res) || '删除失败');
                        return;
                    }
                    this.$message.success('已删除「' + (name || '该任务') + '」');
                    this.deleteVisible = false;
                    this.pendingDelete = null;
                    if (this.records.length === 1 && this.page > 1) this.page -= 1;
                    this.fetchList();
                } catch (e) {
                    this.$message.error('删除失败');
                } finally {
                    this.deleting = false;
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409EFF;
    @primary-light: #ECF5FF;
    @bg: #F5F7FA;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @border-light: #EBEEF5;
    @shadow-light: 0 2px 12px rgba(0, 0, 0, 0.08);
    @radius: 8px;

    .sec {
        min-height: 100%;
    }

    .sec-main {
        max-width: 1400px;
        margin: 0 auto;
        padding: 16px 20px 40px;
    }

    .sec-project-name-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding: 2px 0 4px;
    }

    .sec-project-name-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: @primary-light;
        color: @primary;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .sec-project-name-title {
        font-size: 18px;
        font-weight: 700;
        color: @text-primary;
        line-height: 1.45;
        margin: 0;
        letter-spacing: -0.02em;
        flex: 1;
        min-width: 0;
    }

    .sec-panel {
        background: #fff;
        border-radius: @radius;
        box-shadow: @shadow-light;
        overflow: hidden;
    }

    .sec-panel-header {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 12px;
        padding: 14px 16px;
        border-bottom: 1px solid @border-light;
    }

    .sec-panel-title {
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
    }

    .sec-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 16px;
        background: @bg;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;

        >.el-button {
            flex-shrink: 0;
        }
    }

    .sec-filter-bar {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
        margin-bottom: 0;
        flex-wrap: wrap;

        /deep/ .el-form-item {
            margin-bottom: 0;
            margin-right: 10px;
        }
    }

    .sec-panel-body {
        padding: 0;
        min-height: 200px;
    }

    .sec-name {
        display: block;
        width: 100%;
        color: @text-primary;
        font-weight: 500;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .sec-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    }

    .sec-tag {
        display: inline-block;
        max-width: 100%;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        line-height: 18px;
        background: @primary-light;
        color: @primary;
        border: 1px solid #d9ecff;
        box-sizing: border-box;
    }

    .sec-space-preview {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    .sec-space-line {
        display: flex;
        align-items: center;
        gap: 4px;
        min-width: 0;
    }

    .sec-space-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .sec-space-more {
        flex-shrink: 0;
        margin-left: auto;
        cursor: default;
    }

    .sec-deadline {
        color: @text-regular;
        font-variant-numeric: tabular-nums;
    }

    .sec-submit {
        color: @text-primary;
        font-weight: 500;
    }

    .sec-muted {
        color: @text-secondary;
        font-size: 12px;
    }

    .sec-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        width: 112px;
        margin: 0 auto;
        border: 1px solid @border-light;
        border-radius: 6px;
        overflow: hidden;

        /deep/ .el-button--text {
            margin: 0;
            padding: 4px 0;
            border-radius: 0;
            border-right: 1px solid @border-light;
            border-bottom: 1px solid @border-light;

            &:nth-child(2n) {
                border-right: none;
            }

            &:nth-child(n + 3) {
                border-bottom: none;
            }

            &:hover:not(.is-disabled) {
                background: #f0f7ff;
            }

            &.is-disabled {
                color: #c0c4cc;
            }
        }

        /deep/ .sec-action-delete {
            color: #f56c6c;

            &:hover:not(.is-disabled) {
                background: #fef0f0;
                color: #f56c6c;
            }
        }
    }

    .sec-list-table {
        /deep/ .el-table__body-wrapper {
            overflow-x: hidden;
        }

        /deep/ .el-table__body tr:hover > td {
            background-color: #f5f9ff !important;
        }

        /deep/ .el-table__cell {
            padding: 10px 0;
            vertical-align: middle;
        }

        /deep/ .el-table .cell {
            padding-left: 6px;
            padding-right: 6px;
            line-height: 1.45;
            white-space: normal;
            word-break: break-word;
        }

        /deep/ .sec-name-col .cell {
            padding-left: 16px;
        }

        /deep/ .sec-col-3chars .cell {
            width: 3em;
            box-sizing: content-box;
            margin: 0 auto;
            text-align: center;
        }
    }

    .sec-pagination {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding: 14px 16px;
        border-top: 1px solid @border-light;

        /deep/ .pagination {
            margin: 0;
        }
    }

    .sec-pagination-total {
        font-size: 13px;
        color: @text-secondary;
        line-height: 30px;
    }

    .sec-form-panel {
        padding: 16px 20px 8px;
    }

    .sec-section-title {
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
        margin-bottom: 16px;
    }

    .sec-section-gap {
        margin-top: 20px;
    }

    .sec-space-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .sec-hint {
        margin-top: 6px;
        font-size: 12px;
        color: @text-secondary;
        line-height: 1.5;
    }

    .sec-spec-tabs {
        display: inline-flex;
        border: 1px solid #dcdfe6;
        border-radius: 6px;
        overflow: hidden;
        background: #fff;
    }

    .sec-spec-tab {
        border: none;
        background: transparent;
        padding: 8px 18px;
        font-size: 13px;
        color: @text-regular;
        cursor: pointer;
        font-family: inherit;
        transition: background 0.2s, color 0.2s;
        outline: none;

        & + .sec-spec-tab {
            border-left: 1px solid #dcdfe6;
        }

        &:hover {
            color: @primary;
            background: rgba(64, 158, 255, 0.04);
        }

        &.is-active {
            background: @primary;
            color: #fff;
            font-weight: 500;

            &:hover {
                background: #337ecc;
                color: #fff;
            }
        }
    }

    .sec-spec-panel {
        margin-top: 16px;
    }

    .sec-sub-label {
        font-size: 13px;
        color: @text-primary;
        margin-bottom: 8px;
        line-height: 1.4;
    }

    .sec-req {
        color: #f56c6c;
        margin-right: 4px;
    }

    .sec-import-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 8px;

        .sec-sub-label {
            margin-bottom: 0;
        }
    }

    .sec-import-head-end {
        justify-content: flex-end;
        margin-top: -4px;
        margin-bottom: 0;
    }

    .sec-parsed-fields {
        margin-top: 14px;
        padding: 12px 14px;
        border: 1px solid #e6f0fa;
        border-radius: 8px;
        background: #f8fbff;
    }

    .sec-parsed-label {
        font-size: 13px;
        font-weight: 500;
        color: @text-primary;
        margin-bottom: 8px;
    }

    .sec-parsed-count {
        font-weight: 400;
        color: @text-secondary;
        font-size: 12px;
        margin-left: 6px;
    }

    .sec-parsed-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .sec-parsed-tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        border-radius: 4px;
        font-size: 12px;
        background: #fff;
        color: @primary;
        border: 1px solid #c6e2ff;
    }

    .sec-parsed-idx {
        color: @text-secondary;
        font-size: 11px;
    }

    .sec-upload {
        width: 100%;

        /deep/ .el-upload,
        /deep/ .el-upload-dragger {
            width: 100%;
        }

        /deep/ .el-upload-dragger {
            height: auto;
            min-height: 0;
            padding: 28px 16px 16px;
            line-height: 1.4;
        }

        /deep/ .el-upload__text {
            margin: 0 0 18px;
            line-height: 1.4;
        }
    }

    .sec-upload-icon {
        display: block;
        font-size: 28px;
        color: @primary;
        margin: 0 auto 8px;
        line-height: 1;
    }

    .sec-upload-hint {
        margin-top: 2px;
        font-size: 12px;
        line-height: 1.4;
        color: @text-secondary;
    }

    .sec-file {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border: 1px solid @border-light;
        border-radius: 6px;
        background: #fafafa;

        >i {
            font-size: 22px;
            color: @primary;
        }
    }

    .sec-file-meta {
        flex: 1;
        min-width: 0;
    }

    .sec-file-name {
        font-size: 13px;
        color: @text-primary;
        font-weight: 500;
    }

    .sec-file-status {
        margin-top: 2px;
        font-size: 12px;
        color: @text-secondary;
    }

    .sec-preview {
        margin-top: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        overflow: hidden;
        background: #fff;
        padding: 0 0 10px;
    }

    .sec-preview-hd {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0;
        padding: 10px 14px;
        background: #f8fafc;
        border-bottom: 1px solid #e2e8f0;
        font-size: 13px;
        color: @text-primary;
        font-weight: 500;
    }

    .sec-preview /deep/ .el-table {
        margin: 0;
    }

    .sec-dim-tags {
        margin-top: 8px;
        padding: 0 14px;
    }

    /* 查看回显：只留表头，隐藏空数据占位 */
    .sec-spec-header-only {
        /deep/ .el-table__empty-block,
        /deep/ .el-table__body-wrapper {
            display: none;
        }
    }

    .sec-goal {
        margin: 4px 0 8px 96px;
        padding: 16px 16px 8px;
        background: #f7f9fc;
        border: 1px solid #e8eef5;
        border-radius: 8px;
    }

    .sec-goal-hd {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }

    .sec-goal-title {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
        line-height: 22px;
        white-space: nowrap;
    }

    .sec-goal-desc {
        margin: 4px 0 16px;
        font-size: 12px;
        line-height: 1.6;
        color: #909399;
    }

    .sec-goal-field {
        & + & {
            margin-top: 16px;
        }
    }

    .sec-goal-label {
        font-size: 13px;
        font-weight: 600;
        color: #303133;
        line-height: 20px;
    }

    .sec-goal-hint {
        margin: 2px 0 8px;
        font-size: 12px;
        line-height: 1.5;
        color: #a8abb2;
    }

    .sec-goal-text {
        margin-top: 6px;
        padding: 10px 12px;
        border: 1px solid #e4e7ed;
        border-radius: 4px;
        background: #fff;
        font-size: 13px;
        line-height: 1.7;
        color: #606266;
        white-space: pre-wrap;
        word-break: break-word;
    }

    .sec-goal /deep/ .el-textarea__inner {
        padding: 10px 12px 26px;
        background: #fff;
        border-color: #e4e7ed;
        border-radius: 4px;
        color: #303133;
        font-size: 13px;
        line-height: 1.7;

        &::placeholder {
            color: #c0c4cc;
        }

        &:focus {
            border-color: #409eff;
        }
    }

    .sec-goal /deep/ .el-input__count {
        right: 10px;
        bottom: 6px;
        background: transparent;
        color: #c0c4cc;
        font-size: 12px;
        line-height: 1;
    }

    .sec-goal-view {
        margin-left: 0;
        margin-top: 16px;
    }

    .sec-footer {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 12px 0 8px;
        border-top: 1px solid @border-light;
    }

    /deep/ .el-table {
        border: none;

        &::before {
            display: none;
        }
    }

    /deep/ .el-table__header thead th {
        color: #000;
        font-weight: 400;
        background-color: RGBA(250, 251, 255, 1);
    }

    /* 列表表头覆盖通用表头，细目表预览仍走上面的通用样式 */
    .sec-list-table {
        /deep/ .el-table__header thead th {
            font-size: 13px;
            font-weight: 500;
            color: @text-regular;
            background-color: #f7f8fa;
        }
    }

    /deep/ .el-table__empty-block {
        min-height: 200px;
    }

    .sec-confirm {
        font-size: 14px;
        color: @text-regular;
        line-height: 1.7;
    }
</style>

<style lang="less">
    .sec-confirm-dialog {
        border-radius: 8px;
        overflow: hidden;
    }

    .sec-name-tooltip {
        max-width: 360px;
        line-height: 1.5;
        word-break: break-word;
    }
</style>
